import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import ManagerHeader from '../components/ManagerHeader';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerMobileSidebar from '../components/ManagerMobileSidebar';
import ManagerCreditModal from '../components/ManagerCreditModal';
import PayoutDetailsModal from '../components/PayoutDetailsModal';
import DarkSelect from '../components/DarkSelect';
import Footer from '../components/Footer';
import api from '../services/api';
import { scheduleLoadingEnd } from '../utils/loadingDelay';

interface Payout {
  id: number;
  merchant_id: number;
  amount: number;
  currency: string;
  status: string;
  merchant_reference_number: string;
  viyona_transaction_id: string;
  bank_reference_number: string;
  account_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  customer_email: string;
  customer_phone: string;
  transfer_type: string;
  merchant_name?: string;
  merchant_business_name?: string;
  business_name?: string;
  company_name?: string;
  merchant_company_name?: string;
  name?: string;
  merchant_name_simple?: string;
  merchant_wallet_balance?: number;
  wallet_balance?: number;
  balance?: number;
  created_at: string;
  updated_at: string;
}

interface PayoutFilters {
  status: string;
  fromDate: string;
  toDate: string;
  accountNumber: string;
  transactionId: string;
  merchantReference: string;
  bankReference: string;
  amount: string;
}

const ManagerPayouts: React.FC = () => {
  const { user, logout } = useAuth();
  const { sidebarColor, contentColor, sectionColor, tableColor } = useManagerTheme();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [merchantNames, setMerchantNames] = useState<{[key: number]: string}>({});
  const [merchantWallets, setMerchantWallets] = useState<{[key: number]: number}>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryStats, setSummaryStats] = useState({
    totalDebitsAmount: 0,
    totalPayouts: 0,
    completed: 0,
    pending: 0,
    failed: 0
  });
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showPayoutDetails, setShowPayoutDetails] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<PayoutFilters>({
    status: '',
    fromDate: '',
    toDate: '',
    accountNumber: '',
    transactionId: '',
    merchantReference: '',
    bankReference: '',
    amount: ''
  });
  const loadingStartRef = useRef(0);

  useEffect(() => {
    fetchPayouts();
  }, [filters, currentPage]);

  useEffect(() => {
    fetchSummaryStats();
  }, []);

  const fetchSummaryStats = async () => {
    try {
      // Always fetch global totals without any filters
      const queryParams = new URLSearchParams();
      queryParams.append('limit', '10000'); // Get all payouts for global stats
      
      const response = await api.get(`/dashboard/manager/payouts?${queryParams.toString()}`);
      
      if (response.data.success && response.data.data.payouts) {
        const allPayouts = response.data.data.payouts;
        
        const stats = {
          totalDebitsAmount: allPayouts.reduce((sum: number, payout: Payout) => sum + Number(payout.amount || 0), 0),
          totalPayouts: allPayouts.length,
          completed: allPayouts.filter((p: Payout) => p.status === 'SUCCESS').length,
          pending: allPayouts.filter((p: Payout) => p.status === 'PENDING' || p.status === 'PROCESSING').length,
          failed: allPayouts.filter((p: Payout) => p.status !== 'SUCCESS' && p.status !== 'PENDING' && p.status !== 'PROCESSING').length
        };
        
        console.log('Global summary stats calculated:', stats);
        setSummaryStats(stats);
      } else {
        console.error('Failed to fetch summary stats:', response.data);
      }
    } catch (error) {
      console.error('Error fetching summary stats:', error);
    }
  };

  const fetchMerchantData = async (merchantId: number): Promise<{name: string, walletBalance: number}> => {
    try {
      const response = await api.get(`/merchants/${merchantId}`);
      if (response.data.success && response.data.data) {
        const walletBalance = response.data.data.wallet_balance;
        return {
          name: response.data.data.name || `Merchant #${merchantId}`,
          walletBalance: typeof walletBalance === 'number' ? walletBalance : 
                        typeof walletBalance === 'string' ? parseFloat(walletBalance) || 0 : 0
        };
      }
    } catch (error) {
      console.error(`Error fetching merchant ${merchantId}:`, error);
    }
    return {
      name: `Merchant #${merchantId}`,
      walletBalance: 0
    };
  };

  const fetchMerchantsData = async (payouts: Payout[]) => {
    const uniqueMerchantIds = Array.from(new Set(payouts.map(p => p.merchant_id)));
    const merchantNamesMap: {[key: number]: string} = {};
    const merchantWalletsMap: {[key: number]: number} = {};
    
    // Fetch data for all unique merchant IDs
    await Promise.all(
      uniqueMerchantIds.map(async (merchantId) => {
        const data = await fetchMerchantData(merchantId);
        merchantNamesMap[merchantId] = data.name;
        merchantWalletsMap[merchantId] = data.walletBalance;
      })
    );
    
    setMerchantNames(merchantNamesMap);
    setMerchantWallets(merchantWalletsMap);
  };

  const fetchPayouts = async () => {
    try {
      loadingStartRef.current = Date.now();
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', '50');
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.fromDate) queryParams.append('date_from', filters.fromDate);
      if (filters.toDate) queryParams.append('date_to', filters.toDate);
      if (filters.accountNumber) queryParams.append('account_number', filters.accountNumber);
      if (filters.transactionId) queryParams.append('transaction_id', filters.transactionId);
      if (filters.merchantReference) queryParams.append('merchant_reference', filters.merchantReference);
      if (filters.bankReference) queryParams.append('bank_reference', filters.bankReference);
      if (filters.amount) queryParams.append('amount', filters.amount);

      console.log('🔍 Filters being applied:', filters);
      console.log('🔍 Query params:', queryParams.toString());
      
      const response = await api.get(`/dashboard/manager/payouts?${queryParams.toString()}`);
      
      if (response.data.success) {
        const apiData = response.data.data.payouts || [];
        
        // Transform API data to normalize status values
        const transformedPayouts = apiData.map((payout: any) => {
          // Debug: Log the first payout to see available fields
          if (apiData.indexOf(payout) === 0) {
            console.log('Payout API data fields:', Object.keys(payout));
            console.log('Sample payout data:', payout);
            console.log('Amount field:', payout.amount, 'Type:', typeof payout.amount);
            console.log('Merchant-related fields:', {
              merchant_id: payout.merchant_id,
              merchant_name: payout.merchant_name,
              merchant_business_name: payout.merchant_business_name,
              business_name: payout.business_name,
              company_name: payout.company_name,
              merchant_company_name: payout.merchant_company_name,
              name: payout.name,
              merchant_name_simple: payout.merchant_name_simple
            });
            console.log('Final merchant display will be:', 
              payout.merchant_name || 
              payout.merchant_business_name || 
              payout.business_name || 
              payout.company_name || 
              payout.merchant_company_name ||
              payout.name ||
              payout.merchant_name_simple ||
              (payout.merchant_id ? `Merchant #${payout.merchant_id}` : 'Unknown Merchant')
            );
          }
          
          return {
            ...payout,
            status: payout.status === 'SUCCESS' ? 'SUCCESS' : 
                    payout.status === 'PENDING' || payout.status === 'PROCESSING' ? 'PENDING' : 
                    'FAILED'
          };
        });
        
        setPayouts(transformedPayouts);
        
        // Fetch merchant data for the payouts
        await fetchMerchantsData(transformedPayouts);
        
        // Summary stats remain global - don't update with filters
        
        // Update pagination info
        if (response.data.data.pagination) {
          setTotalPages(response.data.data.pagination.totalPages || 1);
        }
      } else {
        setError('Failed to fetch payouts');
      }
    } catch (err: any) {
      console.error('Error fetching payouts:', err);
      setError('Failed to fetch payouts');
    } finally {
      scheduleLoadingEnd(loadingStartRef.current, setLoading);
    }
  };

  const handleFilterChange = (key: keyof PayoutFilters, value: string) => {
    setCurrentPage(1); // Reset to page 1 when filters change
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setCurrentPage(1); // Reset to page 1 when clearing filters
    setFilters({
      status: '',
      fromDate: '',
      toDate: '',
      accountNumber: '',
      transactionId: '',
      merchantReference: '',
      bankReference: '',
      amount: ''
    });
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'SUCCESS': { color: '#10b981', bg: '#d1fae5', text: 'Success' },
      'PENDING': { color: '#f59e0b', bg: '#fef3c7', text: 'Pending' },
      'PROCESSING': { color: '#3b82f6', bg: '#dbeafe', text: 'Processing' },
      'FAILED': { color: '#ef4444', bg: '#fee2e2', text: 'Failed' },
      'CANCELLED': { color: '#6b7280', bg: '#f3f4f6', text: 'Cancelled' },
      // Legacy lowercase support
      'success': { color: '#10b981', bg: '#d1fae5', text: 'Success' },
      'pending': { color: '#f59e0b', bg: '#fef3c7', text: 'Pending' },
      'processing': { color: '#3b82f6', bg: '#dbeafe', text: 'Processing' },
      'completed': { color: '#10b981', bg: '#d1fae5', text: 'Completed' },
      'failed': { color: '#ef4444', bg: '#fee2e2', text: 'Failed' },
      'cancelled': { color: '#6b7280', bg: '#f3f4f6', text: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING;

    return (
      <span 
        className="inline-block py-1 px-3 rounded-full text-xs font-semibold uppercase"
        style={{ color: config.color, backgroundColor: config.bg }}
      >
        {config.text}
      </span>
    );
  };

  const handleCreditMoney = () => {
    setShowCreditModal(true);
  };

  const handleViewDetails = (payout: Payout) => {
    setSelectedPayout(payout);
    setShowPayoutDetails(true);
  };

  const handleClosePayoutDetails = () => {
    setShowPayoutDetails(false);
    setSelectedPayout(null);
  };

  const handleMyProfile = () => {
    window.location.href = '/manager/account';
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
        <ManagerHeader
          user={user}
          onRefresh={() => {}}
          onCreditMoney={handleCreditMoney}
          onMyProfile={handleMyProfile}
          onLogout={handleLogout}
        />
        <div className="flex flex-1">
          <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab="payouts" /></div>
          <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-8 backdrop-blur-sm text-slate-200" style={{ backgroundColor: contentColor }}>
            <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
            <p className="text-slate-200 text-base m-0">Loading payouts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
      <ManagerMobileSidebar
        showMobileSidebar={showMobileSidebar}
        setShowMobileSidebar={setShowMobileSidebar}
        activeTab="payouts"
        onCreditMoney={handleCreditMoney}
        onLogout={handleLogout}
      />
      <ManagerHeader
        user={user}
        onRefresh={fetchPayouts}
        onCreditMoney={handleCreditMoney}
        onMyProfile={handleMyProfile}
        onLogout={handleLogout}
      />
      <div className="flex flex-1">
        <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab="payouts" /></div>
        <div className="flex-1 flex flex-col text-slate-200 p-6 md:p-8" style={{ backgroundColor: contentColor }}>
          <div className="mb-4">
            <h1 className="text-slate-100 text-2xl font-bold m-0">All My Merchant's Payouts</h1>
            <h3 className="text-slate-300 text-base font-medium mt-1 m-0">Manage and monitor all payout transactions</h3>
          </div>
          <div className="rounded-xl px-4 py-3 mb-6 bg-slate-800/40 border border-white/[0.08]">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2.5 min-w-0 rounded-lg px-3 py-2.5 bg-red-900/60 border border-red-500/40">
                <div className="w-8 h-8 rounded flex items-center justify-center text-lg flex-shrink-0 bg-red-700/80 text-white">💸</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider m-0">
                    Merchant Payout Debits
                    <span className="ml-1 text-[11px] font-bold text-red-200 normal-case">
                      - {formatCurrency(summaryStats.totalDebitsAmount)}
                    </span>
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-2.5 min-w-0 rounded-lg px-3 py-2.5 bg-slate-700/60 border border-white/[0.08]">
                <div className="w-8 h-8 rounded flex items-center justify-center text-lg flex-shrink-0 bg-emerald-700/80 text-white">📊</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider m-0">
                    Total Payouts
                    <span className="ml-1 text-[11px] font-bold text-slate-200 normal-case">
                      - {summaryStats.totalPayouts}
                    </span>
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-2.5 min-w-0 rounded-lg px-3 py-2.5 bg-slate-700/60 border border-white/[0.08]">
                <div className="w-8 h-8 rounded flex items-center justify-center text-lg flex-shrink-0 bg-blue-700/80 text-white">✅</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider m-0">
                    Completed
                    <span className="ml-1 text-[11px] font-bold text-slate-200 normal-case">
                      - {summaryStats.completed}
                    </span>
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-2.5 min-w-0 rounded-lg px-3 py-2.5 bg-slate-700/60 border border-white/[0.08]">
                <div className="w-8 h-8 rounded flex items-center justify-center text-lg flex-shrink-0 bg-amber-700/80 text-white">⏳</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider m-0">
                    Pending
                    <span className="ml-1 text-[11px] font-bold text-slate-200 normal-case">
                      - {summaryStats.pending}
                    </span>
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-2.5 min-w-0 rounded-lg px-3 py-2.5 bg-slate-700/60 border border-white/[0.08]">
                <div className="w-8 h-8 rounded flex items-center justify-center text-lg flex-shrink-0 bg-red-700/80 text-white">❌</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider m-0">
                    Failed
                    <span className="ml-1 text-[11px] font-bold text-slate-200 normal-case">
                      - {summaryStats.failed}
                    </span>
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-6 mb-6 border border-white/[0.06] shadow-xl shadow-black/25" style={{ backgroundColor: sectionColor }}>
            <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-white text-xl font-bold m-0">
                Filter Payouts{' '}
                <span className="font-normal text-white/80 text-base">
                  – Use the filters below to find specific payout transactions
                </span>
              </h3>
              <button
                type="button"
                onClick={handleResetFilters}
                className="mt-2 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 py-2.5 px-5 text-sm font-semibold text-white shadow-lg border-none cursor-pointer sm:mt-0"
              >
                🔄 Reset Filters
              </button>
            </div>
            <div className="flex flex-col gap-8 items-start">
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <input id="fromDate" type="date" value={filters.fromDate} onChange={(e) => handleFilterChange('fromDate', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <input id="toDate" type="date" value={filters.toDate} onChange={(e) => handleFilterChange('toDate', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <DarkSelect
                  id="status"
                  value={filters.status}
                  onChange={(v) => handleFilterChange('status', v)}
                  placeholder="All Status"
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'SUCCESS', label: 'Success' },
                    { value: 'PENDING', label: 'Pending' },
                    { value: 'PROCESSING', label: 'Processing' },
                    { value: 'FAILED', label: 'Failed' },
                    { value: 'CANCELLED', label: 'Cancelled' },
                  ]}
                  className="w-full"
                />
                <input id="amount" type="number" placeholder="Amount" value={filters.amount} onChange={(e) => handleFilterChange('amount', e.target.value)} step="0.01" className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <input id="accountNumber" type="text" placeholder="Account Number" value={filters.accountNumber} onChange={(e) => handleFilterChange('accountNumber', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <input id="transactionId" type="text" placeholder="Transaction ID" value={filters.transactionId} onChange={(e) => handleFilterChange('transactionId', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <input id="merchantReference" type="text" placeholder="Merchant Reference" value={filters.merchantReference} onChange={(e) => handleFilterChange('merchantReference', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                <input id="bankReference" type="text" placeholder="Bank Reference" value={filters.bankReference} onChange={(e) => handleFilterChange('bankReference', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              {/* Reset button moved up next to the title */}
            </div>
          </div>

          {error && (
            <div className="flex flex-col items-center gap-4 py-6 px-6 mb-6 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-700 font-medium m-0">{error}</p>
              <button type="button" onClick={fetchPayouts} className="py-2 px-5 rounded-lg bg-red-600 text-white font-semibold text-sm cursor-pointer border-none">
                Try Again
              </button>
            </div>
          )}

          <div className="flex-1">
            {payouts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 bg-white/[0.06] rounded-2xl border border-white/[0.08]">
                <div className="text-5xl mb-4">💳</div>
                <h3 className="text-xl font-bold text-slate-100 mb-2 m-0">No Payouts Found</h3>
                <p className="text-slate-300 mb-4 m-0">No payout transactions match your current filters.</p>
                <button type="button" onClick={handleResetFilters} className="py-2 px-5 rounded-lg bg-blue-600 text-white font-semibold text-sm cursor-pointer border-none">
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {totalPages > 1 && (
                  <div className="flex justify-between items-center py-5 px-5 mb-4 bg-slate-800/80 rounded-xl shadow border border-white/[0.08]">
                    <div className="flex gap-2 items-center">
                      <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/[0.12] bg-slate-800/80 text-slate-200 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">←</button>
                      {(() => {
                        const pages = [];
                        const maxVisiblePages = 5;
                        if (totalPages <= maxVisiblePages) {
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(<button key={i} type="button" onClick={() => handlePageChange(i)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === i ? 'bg-blue-600 text-white border-blue-600' : 'border border-white/[0.12] bg-slate-800/80 text-slate-200'}`}>{i}</button>);
                          }
                        } else {
                          pages.push(<button key={1} type="button" onClick={() => handlePageChange(1)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === 1 ? 'bg-blue-600 text-white border-blue-600' : 'border border-white/[0.12] bg-slate-800/80 text-slate-200'}`}>1</button>);
                          if (currentPage > 4) pages.push(<span key="ellipsis-start" className="px-2 text-slate-500">...</span>);
                          const startPage = Math.max(2, currentPage - 1);
                          const endPage = Math.min(totalPages - 1, currentPage + 1);
                          for (let i = startPage; i <= endPage; i++) {
                            if (i !== 1 && i !== totalPages) pages.push(<button key={i} type="button" onClick={() => handlePageChange(i)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === i ? 'bg-blue-600 text-white border-blue-600' : 'border border-white/[0.12] bg-slate-800/80 text-slate-200'}`}>{i}</button>);
                          }
                          if (currentPage < totalPages - 3) pages.push(<span key="ellipsis-end" className="px-2 text-slate-500">...</span>);
                          if (totalPages > 1) pages.push(<button key={totalPages} type="button" onClick={() => handlePageChange(totalPages)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === totalPages ? 'bg-blue-600 text-white border-blue-600' : 'border border-white/[0.12] bg-slate-800/80 text-slate-200'}`}>{totalPages}</button>);
                        }
                        return pages;
                      })()}
                      <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/[0.12] bg-slate-800/80 text-slate-200 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">→</button>
                    </div>
                    <div className="text-sm text-slate-600 font-medium">Page {currentPage} of {totalPages}</div>
                  </div>
                )}

                <div className="rounded-2xl overflow-x-auto shadow-lg border border-white/[0.08]" style={{ backgroundColor: tableColor }}>
                  <table className="w-full min-w-[900px] border-collapse text-sm">
                    <thead>
                      <tr className="bg-gradient-to-br from-slate-50 to-slate-100">
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Beneficiary</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Merchant Name</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Merchant Wallet</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Payout Amount</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Transaction Fee</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Payout Account</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Status</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Transaction ID</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Merchant Ref</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Bank Ref</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Created At</th>
                        <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((payout) => (
                        <tr key={payout.id} className="hover:bg-white/[0.06]">
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">
                            <div>
                              <div>{payout.account_name || 'N/A'}</div>
                              <div className="text-slate-400 text-xs">{payout.bank_name || 'N/A'}</div>
                            </div>
                          </td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">
                            <span>
                              {merchantNames[payout.merchant_id] ? 
                                `${merchantNames[payout.merchant_id]} #${payout.merchant_id}` :
                                payout.merchant_name || 
                                payout.merchant_business_name || 
                                payout.business_name || 
                                payout.company_name || 
                                payout.merchant_company_name ||
                                payout.name ||
                                payout.merchant_name_simple ||
                                (payout.merchant_id ? `Merchant #${payout.merchant_id}` : 'Unknown Merchant')
                              }
                            </span>
                          </td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">
                            <span>
                              {merchantWallets[payout.merchant_id] !== undefined ? 
                                `₹${Number(merchantWallets[payout.merchant_id]).toFixed(2)}` :
                                payout.merchant_wallet_balance ? 
                                  `₹${Number(payout.merchant_wallet_balance).toFixed(2)}` : 
                                  payout.wallet_balance ? 
                                    `₹${Number(payout.wallet_balance).toFixed(2)}` :
                                    payout.balance ? 
                                      `₹${Number(payout.balance).toFixed(2)}` :
                                      '₹0.00'
                              }
                            </span>
                          </td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle font-medium">-{formatCurrency(payout.amount)}</td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">
                            {typeof (payout as any).transaction_fee_amount !== 'undefined' && (payout as any).transaction_fee_amount !== null ? formatCurrency(Number((payout as any).transaction_fee_amount) || 0) : formatCurrency(0)}
                          </td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">
                            <div>
                              <div>{payout.account_number || 'N/A'}</div>
                              <div className="text-slate-400 text-xs">{payout.ifsc_code || 'N/A'}</div>
                            </div>
                          </td>
                          <td className="py-4 px-4 border-b border-white/[0.08] align-middle">{getStatusBadge(payout.status)}</td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{payout.viyona_transaction_id || 'N/A'}</td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{payout.merchant_reference_number || 'N/A'}</td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{payout.bank_reference_number || 'N/A'}</td>
                          <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{formatDate(payout.created_at)}</td>
                          <td className="py-4 px-4 border-b border-white/[0.08] align-middle">
                            <button type="button" onClick={() => handleViewDetails(payout)} className="py-2 px-4 rounded-lg bg-blue-600 text-white text-xs font-semibold cursor-pointer border-none hover:bg-blue-700">
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Credit Modal */}
      {showCreditModal && (
        <ManagerCreditModal
          isOpen={showCreditModal}
          onClose={() => setShowCreditModal(false)}
        />
      )}

      {/* Payout Details Modal */}
      <PayoutDetailsModal
        isOpen={showPayoutDetails}
        payout={selectedPayout}
        onClose={handleClosePayoutDetails}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ManagerPayouts;
